/**
 * UserManagement.tsx — Admin surface for platform users.
 *
 * Rebuilt in final pass.
 *
 * Preserves all behavior:
 *   - Paginated user list (GET /api/users) with status + role filters
 *   - Summary metrics: total / active / admins
 *   - Click-to-expand row detail
 *   - Create user modal with role → client assignment rules:
 *     · super_admin can create other super_admins; client_admin cannot
 *     · non-super_admin roles require a client assignment
 *   - Edit user modal (same rules) with active toggle + delete-with-confirm
 *   - POST /api/users, PUT /api/users/{id}, DELETE /api/users/{id}
 *
 * Changes:
 *   - Uses shared UI (Card, Button, Tag, Input, Select, FormField, Modal,
 *     StatCard, Icon, Table, toast)
 *   - Replaces `<>...<tr/><tr/></>` fragment with `React.Fragment` having
 *     a key, fixing the React warning
 *   - Modals use the shared `Modal` component instead of inline overlay
 *   - Role badges use Tag kinds for consistency with Account.tsx
 */

import { Fragment, useEffect, useState, useCallback } from 'react';
import api from '../../api';
import { theme } from '../../theme';
import { useAdmin } from '../../AdminContext';
import {
  Card,
  Button,
  Tag, type TagKind,
  Input, Select, FormField,
  Modal,
  StatCard, StatGrid,
  Icon,
  Skeleton,
  EmptyState,
  Pagination,
  toast,
} from '../../components/ui';

/* ═══════════════ TYPES ═══════════════ */

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  company: string;
  country: string;
  status: string;
  is_admin: boolean;
  client_name: string | null;
  last_login: string | null;
  created_at: string | null;
}

const ROLE_LABELS: Record<string, string> = {
  super_admin:    'Super Admin',
  client_admin:   'Client Admin',
  client_analyst: 'Client Analyst',
  reviewer:       'Reviewer',
  administrator:  'Administrator',
  client:         'Client',
  admin:          'Admin',
};

const ROLE_TAG_KIND: Record<string, TagKind> = {
  super_admin:    'warning',
  client_admin:   'purple',
  client_analyst: 'cyan',
  reviewer:       'success',
  administrator:  'warning',
  admin:          'warning',
};

const LIMIT = 20;

/* ═══════════════ PAGE ═══════════════ */

export default function UserManagement() {
  const { isSuperAdmin } = useAdmin();

  const [items, setItems] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [counts, setCounts] = useState({ total: 0, active: 0, admins: 0 });
  const [loading, setLoading] = useState(true);

  const [clients, setClients] = useState<string[]>([]);

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState({
    email: '', password: '', role: 'client_analyst',
    client_name: '', company: '', country: '',
  });
  const [formError, setFormError] = useState('');
  const [creating, setCreating] = useState(false);

  // Edit modal
  const [editUser, setEditUser] = useState<User | null>(null);
  const [editEmail, setEditEmail] = useState('');
  const [editName, setEditName] = useState('');
  const [editCompany, setEditCompany] = useState('');
  const [editCountry, setEditCountry] = useState('');
  const [editRole, setEditRole] = useState('');
  const [editClient, setEditClient] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [editError, setEditError] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  // Password-reset section state
  const [pwResetLoading, setPwResetLoading] = useState(false);
  const [pwResetTemp, setPwResetTemp] = useState<string | null>(null);

  // Row expand
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'User Management | Meridian Registry';
    return () => { document.title = 'Meridian Registry'; };
  }, []);

  useEffect(() => {
    api.get<string[]>('/api/clients')
      .then(r => setClients(r.data))
      .catch(() => {});
  }, []);

  const fetchData = useCallback(() => {
    setLoading(true);
    const params: Record<string, string | number> = { page, limit: LIMIT };
    if (statusFilter) params.status = statusFilter;
    if (roleFilter) params.role = roleFilter;

    Promise.all([
      api.get<{ items: User[]; total: number }>('/api/users', { params }),
      api.get<{ total: number }>('/api/users', { params: { limit: 1 } }),
      api.get<{ total: number }>('/api/users', { params: { status: 'active', limit: 1 } }),
      api.get<{ items: User[] }>('/api/users', { params: { limit: 100 } }),
    ])
      .then(([listR, allR, activeR, fullR]) => {
        setItems(listR.data.items);
        setTotal(listR.data.total);
        const adminCount = fullR.data.items.filter(u => u.is_admin).length;
        setCounts({
          total: allR.data.total,
          active: activeR.data.total,
          admins: adminCount,
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, statusFilter, roleFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ─── Modal helpers ─── */

  const openCreate = () => {
    setForm({ email: '', password: '', role: 'client_analyst', client_name: '', company: '', country: '' });
    setFormError('');
    setShowCreateModal(true);
  };
  const closeCreate = () => {
    setShowCreateModal(false);
    setFormError('');
  };

  const openEdit = (u: User) => {
    // Populate immediately from the row data so the modal renders without a
    // fetch-flash, then hit GET /api/users/{id} to overlay any fields the row
    // didn't carry (kept in sync in case the row data is stale).
    setEditUser(u);
    setEditEmail(u.email || '');
    setEditName(u.name || '');
    setEditCompany(u.company || '');
    setEditCountry(u.country || '');
    setEditRole(u.role);
    setEditClient(u.client_name || '');
    setEditActive(u.status === 'active');
    setEditError('');
    setDeleteConfirm(false);
    setPwResetTemp(null);

    api.get<User & { is_active?: boolean }>(`/api/users/${u.id}`).then(r => {
      const fresh = r.data;
      setEditEmail(fresh.email || '');
      setEditName(fresh.name || '');
      setEditCompany(fresh.company || '');
      setEditCountry(fresh.country || '');
      setEditRole(fresh.role);
      setEditClient(fresh.client_name || '');
      setEditActive(
        typeof fresh.is_active === 'boolean' ? fresh.is_active : fresh.status === 'active'
      );
      setEditUser(prev => (prev ? { ...prev, ...fresh } : prev));
    }).catch(() => { /* silent — keep row data */ });
  };
  const closeEdit = () => {
    setEditUser(null);
    setEditError('');
    setDeleteConfirm(false);
    setPwResetTemp(null);
  };

  /* ─── Actions ─── */

  const inviteUser = async () => {
    setFormError('');
    if (!form.email) { setFormError('Email is required'); return; }
    if (!form.password) { setFormError('Password is required'); return; }
    if (form.role !== 'super_admin' && !form.client_name) {
      setFormError('Client assignment is required for this role');
      return;
    }

    setCreating(true);
    try {
      await api.post('/api/users', {
        email: form.email,
        password: form.password,
        role: form.role,
        client_name: form.role === 'super_admin' ? null : form.client_name,
        company: form.company,
        country: form.country,
      });
      toast.success('User created', form.email);
      closeCreate();
      fetchData();
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { detail?: string } } };
      if (e.response?.status === 409) setFormError('A user with this email already exists');
      else if (e.response?.data?.detail) setFormError(e.response.data.detail);
      else setFormError('Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const saveEdit = async () => {
    if (!editUser) return;
    if (!editEmail.trim() || !editEmail.includes('@')) {
      setEditError('A valid email is required');
      return;
    }
    if (editRole !== 'super_admin' && !editClient) {
      setEditError('Client assignment is required for non-super-admin roles');
      return;
    }
    setEditSaving(true);
    setEditError('');
    try {
      await api.put(`/api/users/${editUser.id}`, {
        email: editEmail.trim(),
        name: editName,
        company: editCompany,
        country: editCountry,
        role: editRole,
        client_name: editRole === 'super_admin' ? null : editClient,
        is_active: editActive,
      });
      toast.success('User updated');
      closeEdit();
      fetchData();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setEditError(e.response?.data?.detail || 'Failed to update user');
    } finally {
      setEditSaving(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!editUser) return;
    if (!window.confirm(
      `Generate a new temporary password for ${editUser.email}? `
      + `Their existing password will no longer work.`
    )) return;
    setPwResetLoading(true);
    setPwResetTemp(null);
    try {
      const res = await api.post<{ temp_password: string }>(
        '/api/auth/admin-reset-password',
        { user_id: editUser.id },
      );
      setPwResetTemp(res.data.temp_password);
      toast.success('Temporary password generated', 'Share it with the user securely.');
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setEditError(e.response?.data?.detail || 'Failed to reset password');
    } finally {
      setPwResetLoading(false);
    }
  };

  const deleteUser = async () => {
    if (!editUser) return;
    setDeleteLoading(true);
    setEditError('');
    try {
      await api.delete(`/api/users/${editUser.id}`);
      toast.success('User deleted');
      closeEdit();
      fetchData();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setEditError(e.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleteLoading(false);
    }
  };

  const totalPages = Math.ceil(total / LIMIT);
  const formatDate = (d: string | null) => {
    if (!d) return 'Never';
    return new Date(d).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  };

  return (
    <div style={{ padding: 32 }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        marginBottom: 28, flexWrap: 'wrap', gap: 16,
        paddingRight: '3rem', // reserve corridor for fixed profile avatar
      }}>
        <div>
          <h1 style={{
            fontSize: 28, fontWeight: 600,
            color: theme.text.primary, margin: 0,
            letterSpacing: '-0.02em',
          }}>User management</h1>
          <div aria-hidden style={{
            height: 3, width: 40,
            background: theme.brand.cyan,
            borderRadius: 2,
            boxShadow: `0 0 12px ${theme.brand.cyanGlow}`,
            marginTop: 6,
          }} />
          <div style={{
            fontSize: 11, fontWeight: 600,
            color: theme.text.tertiary,
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
            marginTop: 8,
          }}>Manage platform access, roles, and client assignments</div>
        </div>

        <Button
          variant="primary"
          onClick={openCreate}
          leftIcon={<Icon.Plus size={14} />}
        >Create user</Button>
      </div>

      {/* Summary */}
      <StatGrid columns={3}>
        <StatCard label="Total users" value={counts.total.toLocaleString()} primary />
        <StatCard
          label="Active"
          value={counts.active.toLocaleString()}
          delta={`${counts.total ? Math.round((counts.active / counts.total) * 100) : 0}% of total`}
          deltaType="neutral"
        />
        <StatCard label="Admins" value={counts.admins.toLocaleString()} />
      </StatGrid>

      {/* Filters */}
      <div style={{
        display: 'flex', gap: 10,
        margin: '28px 0 18px',
        flexWrap: 'wrap',
      }}>
        <div style={{ minWidth: 180 }}>
          <Select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Select>
        </div>
        <div style={{ minWidth: 200 }}>
          <Select
            value={roleFilter}
            onChange={e => { setRoleFilter(e.target.value); setPage(1); }}
          >
            <option value="">All roles</option>
            <option value="super_admin">Super admin</option>
            <option value="client_admin">Client admin</option>
            <option value="client_analyst">Client analyst</option>
            <option value="reviewer">Reviewer</option>
          </Select>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <Card>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} style={{
              display: 'flex', gap: 16, alignItems: 'center',
              padding: '10px 0',
              borderBottom: i === 5 ? 'none' : `1px solid ${theme.borderColor.subtle}`,
            }}>
              <Skeleton width={32} height={32} />
              <div style={{ flex: 1 }}>
                <Skeleton width={`${60 + (i * 7) % 30}%`} height={14} />
              </div>
              <Skeleton width={80} height={18} />
            </div>
          ))}
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Icon.Users size={24} />}
            title="No users"
            description="Create your first user to get started."
            action={
              <Button variant="primary" leftIcon={<Icon.Plus size={14} />} onClick={openCreate}>
                Create user
              </Button>
            }
          />
        </Card>
      ) : (
        <>
          {/* Custom table with expansion — shared Table doesn't support expand-rows yet */}
          <Card padding="none">
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 13,
              }}>
                <thead>
                  <tr style={{ background: theme.surface.card }}>
                    {['', 'Name', 'Role', 'Client', 'Company', 'Status', 'Last login', ''].map((h, i) => (
                      <th key={i} style={{
                        textAlign: 'left',
                        padding: '10px 14px',
                        color: theme.text.tertiary,
                        fontWeight: 600,
                        fontSize: 11,
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        borderBottom: `1px solid ${theme.borderColor.default}`,
                        whiteSpace: 'nowrap',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((u, i) => {
                    const expanded = expandedId === u.id;
                    return (
                      <Fragment key={u.id}>
                        <tr
                          onClick={() => setExpandedId(prev => prev === u.id ? null : u.id)}
                          onMouseEnter={e => { e.currentTarget.style.background = theme.surface.cardHover; }}
                          onMouseLeave={e => {
                            e.currentTarget.style.background = i % 2 === 0
                              ? theme.surface.panel
                              : theme.surface.card;
                          }}
                          style={{
                            background: i % 2 === 0 ? theme.surface.panel : theme.surface.card,
                            cursor: 'pointer',
                            transition: 'background 120ms ease',
                          }}>
                          {/* Avatar */}
                          <td style={tdStyle}>
                            <div style={{
                              width: 32, height: 32, borderRadius: '50%',
                              background: theme.brand.cyanSoft,
                              border: `1px solid ${theme.borderColor.brand}`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: theme.brand.cyan,
                              fontSize: 13, fontWeight: 700,
                              fontFamily: theme.font.data,
                            }}>{(u.name || u.email).charAt(0).toUpperCase()}</div>
                          </td>
                          {/* Name + email */}
                          <td style={tdStyle}>
                            <div style={{ fontWeight: 500, color: theme.text.primary }}>
                              {u.name || u.email.split('@')[0]}
                            </div>
                            <div style={{
                              color: theme.text.tertiary,
                              fontSize: 12,
                              marginTop: 2,
                            }}>{u.email}</div>
                          </td>
                          {/* Role */}
                          <td style={tdStyle}>
                            <Tag kind={ROLE_TAG_KIND[u.role] ?? 'neutral'} size="sm">
                              {ROLE_LABELS[u.role] || u.role}
                            </Tag>
                          </td>
                          {/* Client */}
                          <td style={{
                            ...tdStyle,
                            fontFamily: theme.font.data,
                            fontSize: 12,
                          }}>
                            {u.client_name || (
                              <span style={{
                                color: theme.text.tertiary,
                                fontStyle: 'italic',
                              }}>All</span>
                            )}
                          </td>
                          {/* Company */}
                          <td style={tdStyle}>
                            <span style={{ color: theme.text.secondary }}>{u.company || '—'}</span>
                          </td>
                          {/* Status */}
                          <td style={tdStyle}>
                            <Tag
                              kind={u.status === 'active' ? 'success' : 'neutral'}
                              size="sm"
                            >
                              {u.status === 'active' ? 'Active' : 'Inactive'}
                            </Tag>
                          </td>
                          {/* Last login */}
                          <td style={{
                            ...tdStyle,
                            fontFamily: theme.font.data,
                            fontSize: 12,
                            color: u.last_login ? theme.text.primary : theme.text.tertiary,
                          }}>{formatDate(u.last_login)}</td>
                          {/* Edit */}
                          <td style={tdStyle}>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); openEdit(u); }}
                            >Edit</Button>
                          </td>
                        </tr>
                        {expanded && (
                          <tr style={{ background: theme.brand.cyanSoft }}>
                            <td colSpan={8} style={{
                              padding: '18px 24px',
                              borderBottom: `1px solid ${theme.borderColor.default}`,
                            }}>
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                columnGap: 32,
                                rowGap: 14,
                              }}>
                                {/* Email gets its own full-width row so long addresses don't collide with neighbours */}
                                <div style={{ gridColumn: '1 / -1' }}>
                                  <DetailField label="Email" value={u.email} mono />
                                </div>
                                <DetailField label="Role">
                                  <Tag kind={ROLE_TAG_KIND[u.role] ?? 'neutral'} size="sm">
                                    {ROLE_LABELS[u.role] || u.role}
                                  </Tag>
                                </DetailField>
                                <DetailField label="Status">
                                  <Tag
                                    kind={u.status === 'active' ? 'success' : 'neutral'}
                                    size="sm"
                                  >{u.status === 'active' ? 'Active' : 'Inactive'}</Tag>
                                </DetailField>
                                <DetailField label="Client" value={u.client_name || 'All clients'} />
                                <DetailField label="Company" value={u.company || '—'} />
                                <DetailField label="Country" value={u.country || '—'} />
                                <DetailField label="Last login" value={formatDate(u.last_login)} />
                                <DetailField label="Created" value={formatDate(u.created_at)} />
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {totalPages > 1 && (
            <div style={{
              display: 'flex', justifyContent: 'center',
              marginTop: 20,
            }}>
              <Pagination page={page} totalPages={totalPages} onChange={setPage} />
            </div>
          )}
        </>
      )}

      {/* Create modal */}
      <Modal
        open={showCreateModal}
        onClose={closeCreate}
        title="Create user"
        description="Grant access to the Meridian platform."
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <Button variant="ghost" onClick={closeCreate}>Cancel</Button>
            <Button
              variant="primary"
              loading={creating}
              onClick={inviteUser}
            >Create user</Button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <FormField label="Email" required>
            <Input
              type="email"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder="user@company.com"
            />
          </FormField>
          <FormField label="Password" required>
            <Input
              type="password"
              value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              placeholder="Secure password"
            />
          </FormField>
          <FormField label="Role" required>
            <Select
              value={form.role}
              onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
            >
              <option value="client_analyst">Client analyst</option>
              <option value="client_admin">Client admin</option>
              <option value="reviewer">Reviewer</option>
              {isSuperAdmin && <option value="super_admin">Super admin</option>}
            </Select>
          </FormField>
          {form.role !== 'super_admin' && (
            <FormField label="Client assignment" required>
              <Select
                value={form.client_name}
                onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))}
              >
                <option value="">Select client…</option>
                {clients.map(c => <option key={c} value={c}>{c}</option>)}
              </Select>
            </FormField>
          )}
          <FormField label="Company (optional)">
            <Input
              value={form.company}
              onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
              placeholder="Company name"
            />
          </FormField>
          {formError && (
            <div style={{
              fontSize: 13,
              color: theme.semantic.danger,
              background: theme.semantic.dangerSoft,
              border: `1px solid ${theme.semantic.dangerBorder}`,
              borderRadius: 6,
              padding: '8px 12px',
            }}>{formError}</div>
          )}
        </div>
      </Modal>

      {/* Edit modal */}
      <Modal
        open={!!editUser}
        onClose={closeEdit}
        title={editUser ? `Edit ${editUser.email}` : 'Edit user'}
        footer={
          deleteConfirm ? null : (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <Button variant="ghost" onClick={closeEdit}>Cancel</Button>
              <Button
                variant="primary"
                loading={editSaving}
                onClick={saveEdit}
              >Save changes</Button>
            </div>
          )
        }
      >
        {editUser && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Email — full width, editable only for super_admin, with warning */}
            <FormField label="Email" required>
              <Input
                type="email"
                value={editEmail}
                onChange={e => setEditEmail(e.target.value)}
                disabled={!isSuperAdmin}
                autoComplete="off"
              />
              {isSuperAdmin && (
                <p style={{
                  fontSize: 11,
                  color: theme.semantic.warning,
                  marginTop: 6,
                  marginBottom: 0,
                  lineHeight: 1.4,
                }}>
                  Changing the email changes how this user logs in. For Auth0 users
                  the email must match their SSO identity.
                </p>
              )}
            </FormField>

            {/* Grid: two columns on wider modals, stack on narrow */}
            <div
              className="meridian-edit-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 14,
              }}>
              <style>{`
                @media (max-width: 520px) {
                  .meridian-edit-grid { grid-template-columns: 1fr !important; }
                }
              `}</style>

              <FormField label="Name">
                <Input
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  placeholder="Full name"
                />
              </FormField>

              <FormField label="Company">
                <Input
                  value={editCompany}
                  onChange={e => setEditCompany(e.target.value)}
                  placeholder="Company name"
                />
              </FormField>

              <FormField label="Role">
                <Select
                  value={editRole}
                  onChange={e => setEditRole(e.target.value)}
                >
                  <option value="client_analyst">Client analyst</option>
                  <option value="client_admin">Client admin</option>
                  <option value="reviewer">Reviewer</option>
                  {isSuperAdmin && <option value="super_admin">Super admin</option>}
                </Select>
              </FormField>

              {editRole !== 'super_admin' ? (
                <FormField label="Client assignment" required>
                  <Select
                    value={editClient}
                    onChange={e => setEditClient(e.target.value)}
                  >
                    <option value="">Select client…</option>
                    {clients.map(c => <option key={c} value={c}>{c}</option>)}
                  </Select>
                </FormField>
              ) : (
                <FormField label="Country">
                  <Input
                    value={editCountry}
                    onChange={e => setEditCountry(e.target.value)}
                    placeholder="Country"
                  />
                </FormField>
              )}

              {editRole !== 'super_admin' && (
                <FormField label="Country">
                  <Input
                    value={editCountry}
                    onChange={e => setEditCountry(e.target.value)}
                    placeholder="Country"
                  />
                </FormField>
              )}

              <FormField label="Status">
                <button
                  type="button"
                  onClick={() => setEditActive(!editActive)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: 8,
                    background: editActive ? theme.semantic.successSoft : theme.surface.inset,
                    border: `1px solid ${editActive ? theme.semantic.successBorder : theme.borderColor.default}`,
                    color: editActive ? theme.semantic.success : theme.text.tertiary,
                    fontFamily: theme.font.ui,
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: 'pointer',
                    transition: 'all 120ms ease',
                    textAlign: 'left',
                  }}>
                  {editActive ? '● Active' : '○ Inactive'}
                </button>
              </FormField>
            </div>

            {/* Read-only metadata */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 10,
              padding: '12px 14px',
              background: 'rgba(255,255,255,0.02)',
              border: `1px solid ${theme.borderColor.subtle}`,
              borderRadius: 8,
            }}>
              <div>
                <div style={metaLabelStyle}>Last login</div>
                <div style={metaValueStyle}>{formatDate(editUser.last_login)}</div>
              </div>
              <div>
                <div style={metaLabelStyle}>Member since</div>
                <div style={metaValueStyle}>{formatDate(editUser.created_at)}</div>
              </div>
            </div>

            {editError && (
              <div style={{
                fontSize: 13,
                color: theme.semantic.danger,
                background: theme.semantic.dangerSoft,
                border: `1px solid ${theme.semantic.dangerBorder}`,
                borderRadius: 6,
                padding: '8px 12px',
              }}>{editError}</div>
            )}

            {/* Password reset */}
            <div style={{
              borderTop: `1px solid ${theme.borderColor.subtle}`,
              marginTop: 6, paddingTop: 14,
            }}>
              <Button
                variant="secondary"
                fullWidth
                leftIcon={<Icon.Refresh size={14} />}
                loading={pwResetLoading}
                onClick={handlePasswordReset}
              >Send password reset</Button>
              <p style={{
                fontSize: 11,
                color: theme.text.tertiary,
                marginTop: 6,
                marginBottom: 0,
                lineHeight: 1.4,
              }}>
                Generates a new temporary password. Share it with the user securely; Auth0 SSO accounts should use their provider's reset instead.
              </p>
              {pwResetTemp && (
                <div style={{
                  marginTop: 10,
                  padding: '10px 12px',
                  background: theme.semantic.warningSoft,
                  border: `1px solid ${theme.semantic.warningBorder}`,
                  borderRadius: 6,
                }}>
                  <div style={{
                    fontSize: 10,
                    color: theme.semantic.warning,
                    letterSpacing: theme.letterSpacing.wide,
                    textTransform: 'uppercase',
                    fontWeight: theme.fontWeight.semibold,
                    marginBottom: 4,
                  }}>Temporary password (shown once)</div>
                  <code style={{
                    fontFamily: theme.font.data,
                    fontSize: 14,
                    color: theme.text.primary,
                    userSelect: 'all',
                    wordBreak: 'break-all',
                  }}>{pwResetTemp}</code>
                </div>
              )}
            </div>

            {/* Delete section */}
            <div style={{
              borderTop: `1px solid ${theme.borderColor.subtle}`,
              marginTop: 6, paddingTop: 14,
            }}>
              {!deleteConfirm ? (
                <Button
                  variant="danger"
                  fullWidth
                  leftIcon={<Icon.Trash size={14} />}
                  onClick={() => setDeleteConfirm(true)}
                >Delete user</Button>
              ) : (
                <div style={{
                  background: theme.semantic.dangerSoft,
                  border: `1px solid ${theme.semantic.dangerBorder}`,
                  borderRadius: 8,
                  padding: 14,
                }}>
                  <div style={{
                    color: theme.semantic.danger,
                    fontSize: 13, fontWeight: 600,
                    marginBottom: 10,
                    display: 'flex', gap: 8, alignItems: 'flex-start',
                  }}>
                    <Icon.AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                    <span>Are you sure you want to permanently delete this user? This cannot be undone.</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                      variant="danger"
                      fullWidth
                      loading={deleteLoading}
                      onClick={deleteUser}
                    >Confirm delete</Button>
                    <Button
                      variant="ghost"
                      fullWidth
                      onClick={() => setDeleteConfirm(false)}
                    >Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

/* ═══════════════ SUB-COMPONENTS ═══════════════ */

const tdStyle: React.CSSProperties = {
  padding: '10px 14px',
  borderBottom: `1px solid ${theme.borderColor.subtle}`,
  verticalAlign: 'middle',
};

const metaLabelStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  color: theme.text.tertiary,
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  marginBottom: 4,
};

const metaValueStyle: React.CSSProperties = {
  fontFamily: theme.font.data,
  fontSize: 12,
  color: theme.text.secondary,
};

function DetailField({
  label, value, mono, children,
}: {
  label: string;
  value?: string;
  mono?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div>
      <div style={{
        fontSize: 10, fontWeight: 600,
        color: theme.text.tertiary,
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        marginBottom: 4,
      }}>{label}</div>
      {children || (
        <div style={{
          color: theme.text.primary,
          fontSize: 13,
          fontFamily: mono ? theme.font.data : theme.font.ui,
        }}>{value}</div>
      )}
    </div>
  );
}
